/**
 * Health bands, client side.
 *
 * ⚠️  MIRRORS apps/customer-management-api/src/lib/health.ts — KEEP THE TWO IN
 * SYNC. Same arrangement as auth/roles.ts, which carries the same warning for
 * the same reason: the two apps deploy separately and cannot import each other,
 * so the shared vocabulary is duplicated deliberately rather than smuggled
 * through a package nobody remembers to version.
 *
 * WHAT IS *NOT* DUPLICATED: the banding itself. `computeHealth` lives only on
 * the server and every band on screen arrives on the row from /api/customers.
 * That is the entire point of the 2026-09-08 change — health used to be
 * computed in /api/account-health and hardcoded `null` in /api/customers, and
 * Home read both, so it rendered "All customers in healthy bands / 0" beside a
 * bar saying 61% Critical. Re-deriving a band in the browser would recreate
 * exactly that class of disagreement.
 *
 * So this file holds only presentation vocabulary: which bands exist, which of
 * them are a judged relationship, and how to roll a list up for a chart.
 */

export type HealthBand =
  | 'Critical'
  | 'At Risk'
  | 'Stable'
  | 'Growing'
  | 'New'
  | 'Dormant';

/** Display order for legends and bars. */
export const HEALTH_BANDS: HealthBand[] = [
  'Growing',
  'Stable',
  'At Risk',
  'Critical',
  'New',
  'Dormant',
];

/**
 * Bands describing a LIVE, JUDGED relationship.
 *
 * `New` has no trend to judge yet and `Dormant` has no relationship, so they
 * report counts but take no share of the distribution. This is what stops a
 * definitional bucket owning the chart: 1,476 dormant records rendered as 61%
 * Critical and squashed every actionable band into a sliver nobody could read.
 */
export const SCORED_BANDS: HealthBand[] = [
  'Growing',
  'Stable',
  'At Risk',
  'Critical',
];

export function isScoredBand(band: string | null | undefined): boolean {
  return !!band && (SCORED_BANDS as string[]).includes(band);
}

/** The bands that mean "somebody should do something". */
export const ATTENTION_BANDS: HealthBand[] = ['Critical', 'At Risk'];

export function needsAttention(band: string | null | undefined): boolean {
  return !!band && (ATTENTION_BANDS as string[]).includes(band);
}

/** CSS custom property per band, so the bar and the pills cannot drift apart. */
export const BAND_COLOR: Record<HealthBand, string> = {
  Growing: 'var(--green)',
  Stable: 'var(--blue)',
  'At Risk': 'var(--amber)',
  Critical: 'var(--red)',
  // Neutral on purpose: New and Dormant are states, not severities, and
  // colouring them red or green would put them back in the ranking.
  New: 'var(--blue)',
  Dormant: 'var(--border-light)',
};

/** The class name the existing .health-pill styles expect. */
export function bandPillClass(band: string | null | undefined): string {
  return `health-pill ${(band || '').toLowerCase().replace(/\s+/g, '_')}`;
}

export interface BandSummaryRow {
  band: HealthBand;
  count: number;
  /** Share of the SCORED population. 0 for New and Dormant, by design. */
  pct: number;
  totalSpend: number;
  avgScore: number | null;
  scored: boolean;
}

/**
 * Roll a list of rows up for the portfolio bar.
 *
 * Mirrors summarizeBands in the server module — same denominator rule, same
 * avgScore handling (unscored rows are ignored, never counted as zero).
 */
export function summarizeBands(
  rows: ReadonlyArray<{
    health_band?: string | null;
    health_score?: number | null;
    total_spend_12m?: number | null;
  }>,
): { summary: BandSummaryRow[]; scoredTotal: number; total: number } {
  const scoredTotal = rows.filter((r) => isScoredBand(r.health_band)).length;

  const summary = HEALTH_BANDS.map((band) => {
    const inBand = rows.filter((r) => r.health_band === band);
    const scored = (SCORED_BANDS as string[]).includes(band);
    const withScore = inBand.filter((r) => r.health_score != null) as Array<{
      health_score: number;
    }>;
    return {
      band,
      count: inBand.length,
      pct:
        scored && scoredTotal > 0
          ? Math.round((inBand.length / scoredTotal) * 100)
          : 0,
      totalSpend: inBand.reduce((s, r) => s + (r.total_spend_12m ?? 0), 0),
      avgScore:
        withScore.length === 0
          ? null
          : Math.round(
              withScore.reduce((s, r) => s + r.health_score, 0) /
                withScore.length,
            ),
      scored,
    };
  });

  return { summary, scoredTotal, total: rows.length };
}
